import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { accessRightDefinition } from '../../../../../entity-definitions/access-right.definition';
import { EntityDialogService } from '../../../../../services/entity-dialog.service';
import { AccessRightDomainStore, AccessRightStore } from '../../../../../stores/access-right.store';
import { AssetGroupStore } from '../../../../../stores/asset-group.store';
import { AssetStore } from '../../../../../stores/asset.store';
import { RoleStore } from '../../../../../stores/role.store';
import { UserStore } from '../../../../../stores/user.store';
import { ToastService } from '../../../../../services/toast.service';
import { AccessRight, AccessRightDomain } from '../../../../../types/access-right.interface';
import { Asset } from '../../../../../types/asset.interface';
import { EntityFormResult, isEntityFormDeleteAction } from '../../../../../types/entity-form-result.interface';
import { User } from '../../../../../types/user.interface';
import { groupAssetsByAssetGroup } from '../../../../../utils/asset-grouping.util';
import { GenericParentGridComponent } from '../../../../generic/generic-parent-grid.component';
import { CesButtonTertiaryComponent } from '../../../../ui/ces-buttons/ces-button-tertiary.component';
import { CesButtonComponent } from '../../../../ui/ces-buttons/ces-button.component';
import { CesIconComponent } from '../../../../ui/ces-icon/ces-icon.component';

@Component({
  selector: 'app-user-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BreadcrumbModule,
    CesIconComponent,
    DatePipe,
    GenericParentGridComponent,
    CesButtonComponent,
    CesButtonTertiaryComponent,
    TranslocoModule,
  ],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.css',
})
export class UserDetailComponent implements OnInit {
  menuItems: MenuItem[] = [];

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  protected readonly userStore = inject(UserStore);
  protected readonly accessRightDomainStore = inject(AccessRightDomainStore);
  protected readonly accessRightStore = inject(AccessRightStore);

  protected readonly assetStore = inject(AssetStore);
  protected readonly assetGroupStore = inject(AssetGroupStore);
  protected readonly roleStore = inject(RoleStore);
  private readonly dialogService = inject(EntityDialogService);
  private readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslocoService);

  dataLoadingCompleted: boolean = false;
  loadFailed: boolean = false;
  user: User = <User>{};

  accessRightsDomain: AccessRightDomain[] = [];
  accessRightDefinition = accessRightDefinition;
  selectedIds: number[] = [];

  selectedAccessRightIds: number[] = [];
  showBulkEditButton: boolean = false;

  ngOnInit(): void {
    this.menuItems.push({
      label: this.translateService.translate('entities.user.breadcrumb'),
      routerLink: '/configuration/access-rights/users',
    });
    void this.loadReferenceData();
    void this.loadData();
  }

  private async loadReferenceData() {
    try {
      await Promise.all([this.roleStore.loadAll(), this.assetStore.loadAll(), this.assetGroupStore.loadAll()]);
    } catch (error) {
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    }
  }

  private buildAvailableGroupedAssets(userId: number) {
    const assignedAssetIds = new Set(this.accessRightsDomain.filter(ar => ar.userId === userId).map(ar => ar.assetId));
    const available = this.assetStore.entities().filter(asset => !assignedAssetIds.has(asset.id));
    return groupAssetsByAssetGroup(available, this.assetGroupStore.entities());
  }

  private async loadData() {
    this.loadFailed = false;
    try {
      await this.userStore.loadAll();

      const users = this.userStore.entities();
      const idParam = this.route.snapshot.paramMap.get('id');
      const id = idParam !== null ? Number(idParam) : null;
      const matched = id !== null && !Number.isNaN(id) ? users.find(o => o.id === id) : undefined;
      if (!matched) {
        this.loadFailed = true;
        return;
      }
      this.user = matched;

      await this.accessRightDomainStore.loadByUserId(this.user.id);

      this.accessRightsDomain = this.accessRightDomainStore.entities();

      this.dataLoadingCompleted = true;
    } catch (error) {
      this.loadFailed = true;
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    } finally {
      this.cdr.markForCheck();
    }
  }

  goBackToUsers(): void {
    void this.router.navigate(['/configuration/access-rights/users']);
  }

  createAccessRight() {
    this.dialogService
      .openCreateDialog<AccessRightDomain>({
        header: this.accessRightDefinition.messages.dialogTitles.create(),
        headerParams: { name: this.user.displayName },
        entityDefinition: this.accessRightDefinition,
        mode: 'create',
        additionalData: {
          Origin: 'settings',
          Role: this.roleStore.sortedByName(),
          Asset: this.assetStore.entities(),
          GroupedAssets: this.buildAvailableGroupedAssets(this.user.id),
          User: [this.user],
          AccessRights: this.accessRightsDomain,
        },
      })
      .subscribe(formData => {
        if (!formData || isEntityFormDeleteAction(formData)) {
          return;
        }
        void (async () => {
          try {
            if (formData['syncMode']) {
              const result = await this.accessRightDomainStore.sync({
                userId: formData['userId'] as number,
                roleId: formData['roleId'] as number,
                toAdd: formData['toAdd'] as number[],
                toRemove: formData['toRemove'] as number[],
              });
              this.toastService.success('entities.access.messages.syncSuccess', false, undefined, undefined, {
                added: result.added,
                removed: result.removed,
              });
              await this.loadData();
            } else {
              const payload: Omit<AccessRight, 'id'> = {
                name: formData['name'] ?? '',
                assetId: formData['assetId'] as number,
                userId: formData['userId'] as number,
                roleId: formData['roleId'] as number,
              };
              await this.accessRightStore.create(payload);
              await this.loadData();
            }
          } catch (error) {
            console.error(this.translateService.translate('common.errors.accessCreationFailed'), error);
            this.toastService.error('common.errors.accessCreationFailed');
          }
        })();
      });
  }

  editAccessRight(accessRightDomain: AccessRightDomain) {
    void this.accessRightStore.loadById(accessRightDomain.id);
    this.dialogService
      .openEditDialog<AccessRightDomain>(accessRightDomain, {
        header: this.accessRightDefinition.messages.dialogTitles.edit(),
        headerParams: { name: this.user.displayName },
        entityDefinition: this.accessRightDefinition,
        mode: 'edit',
        additionalData: {
          Origin: 'settings',
          Role: this.roleStore.sortedByName(),
          Asset: this.assetStore.entities(),
          User: [this.user],
          AccessRights: this.accessRightsDomain,
          SelectedIds: this.selectedIds,
        },
        onDelete: entity => this.deleteAccessRight(entity),
      })
      .subscribe(result => {
        if (!result) {
          return;
        }
        if (isEntityFormDeleteAction(result)) {
          this.deleteAccessRight(result.entity);
          return;
        }
        void (async () => {
          try {
            await this.accessRightStore.update(accessRightDomain.id, result);
          } catch (error) {
            console.error(this.translateService.translate('common.errors.accessUpdateFailed'), error);
            this.toastService.error('common.errors.accessUpdateFailed');
          } finally {
            await this.loadData();
          }
        })();
      });
  }
  deleteAccessRight(accessRightDomain: AccessRightDomain) {
    void (async () => {
      try {
        await this.accessRightStore.delete(accessRightDomain.id);
      } catch (error) {
        console.error(this.translateService.translate('common.errors.accessDeletionFailed'), error);
        this.toastService.error('common.errors.accessDeletionFailed');
      } finally {
        await this.loadData();
      }
    })();
  }

  onSelectionChange(selectedIds: number[]) {
    this.selectedAccessRightIds = selectedIds;
    this.showBulkEditButton = selectedIds.length > 0;
  }

  bulkEditAccessRights() {
    if (this.selectedAccessRightIds.length === 0) return;

    if (this.selectedAccessRightIds.length === 1) {
      this.handleSingleEdit();
    } else {
      this.handleBulkEdit();
    }
  }

  private handleSingleEdit() {
    const selectedAccessRight = this.accessRightsDomain.find(ar => ar.id === this.selectedAccessRightIds[0]);

    if (!selectedAccessRight) return;

    void this.accessRightStore.loadById(selectedAccessRight.id);
    const dialogOptions = this.createEditDialogOptions(
      this.translateService.translate('entities.user.messages.editRoleFor', {
        name: selectedAccessRight.assetName ?? '',
      }),
      'edit',
      this.assetStore.entities(),
    );

    this.dialogService
      .openEditDialog<AccessRightDomain>(selectedAccessRight, dialogOptions)
      .subscribe(result => this.handleEditResult(result, selectedAccessRight.id));

    this.clearSelection();
  }

  private handleBulkEdit() {
    const selectedAccessRights = this.accessRightsDomain.filter(ar => this.selectedAccessRightIds.includes(ar.id));

    const selectedAssets = selectedAccessRights
      .map(ar => this.assetStore.entities().find((asset: Asset) => asset.id === ar.assetId))
      .filter(asset => asset !== undefined);

    const dialogOptions = this.createEditDialogOptions(
      this.translateService.translate('entities.user.messages.editRoleForMultiple', { count: this.selectedAccessRightIds.length }),
      'bulk-edit',
      selectedAssets,
      selectedAccessRights,
    );

    const primary = selectedAccessRights[0];
    if (!primary) return;
    this.dialogService.openEditDialog<AccessRightDomain>(primary, dialogOptions).subscribe(result => {
      if (!result || isEntityFormDeleteAction(result)) {
        return;
      }
      const roleId = result['roleId'];
      if (typeof roleId === 'number') {
        void this.updateMultipleAccessRights(roleId);
      }
    });
  }

  private createEditDialogOptions(
    header: string,
    mode: 'edit' | 'bulk-edit',
    assets: Asset[],
    selectedAccessRights?: AccessRightDomain[],
  ) {
    const baseData = {
      Origin: 'settings',
      Role: this.roleStore.sortedByName(),
      Asset: assets,
      User: [this.user],
      AccessRights: this.accessRightsDomain,
    };

    return {
      header,
      entityDefinition: this.accessRightDefinition,
      mode,
      additionalData: selectedAccessRights ? { ...baseData, SelectedAccessRights: selectedAccessRights } : baseData,
      ...(mode === 'edit' && { onDelete: (entity: AccessRightDomain) => this.deleteAccessRight(entity) }),
    };
  }

  private handleEditResult(result: EntityFormResult<AccessRightDomain> | undefined, accessRightId: number) {
    if (!result) return;

    if (isEntityFormDeleteAction(result)) {
      this.deleteAccessRight(result.entity);
      return;
    }
    void (async () => {
      try {
        await this.accessRightStore.update(accessRightId, result);
      } catch (error) {
        console.error(this.translateService.translate('common.errors.accessUpdateFailed'), error);
        this.toastService.error('common.errors.accessUpdateFailed');
      } finally {
        await this.loadData();
      }
    })();
  }

  private async updateMultipleAccessRights(roleId: number) {
    try {
      for (const accessRightId of this.selectedAccessRightIds) {
        const accessRight = this.accessRightsDomain.find(ar => ar.id === accessRightId);
        if (accessRight) {
          await this.accessRightStore.loadById(accessRightId);
          await this.accessRightStore.update(accessRightId, {
            id: accessRight.id,
            assetId: accessRight.assetId,
            userId: accessRight.userId,
            roleId: roleId,
          });
        }
      }
      await this.loadData();
      this.clearSelection();
    } catch (error) {
      console.error(this.translateService.translate('common.errors.accessUpdateFailed'), error);
      this.toastService.error('common.errors.accessUpdateFailed');
    }
  }

  private clearSelection() {
    this.selectedAccessRightIds = [];
    this.showBulkEditButton = false;
  }

  cancelBulkEdit() {
    this.clearSelection();
  }

  bulkDeleteAccessRights() {
    if (this.selectedAccessRightIds.length === 0) return;

    const selectedAccessRights = this.accessRightsDomain.filter(ar => this.selectedAccessRightIds.includes(ar.id));

    // Open bulk delete confirmation dialog
    this.dialogService
      .openBulkDeleteDialog({
        header: this.translateService.translate('entities.user.messages.deleteAccessCount', {
          count: this.selectedAccessRightIds.length,
        }),
        entityDefinition: this.accessRightDefinition,
        mode: 'bulk-delete',
        additionalData: {
          accessRights: selectedAccessRights,
        },
      })
      .subscribe(confirmed => {
        if (!confirmed) return;
        void (async () => {
          try {
            for (const accessRightId of this.selectedAccessRightIds) {
              await this.accessRightStore.delete(accessRightId);
            }

            await this.loadData();
            this.selectedAccessRightIds = [];
            this.showBulkEditButton = false;
          } catch (error) {
            console.error(this.translateService.translate('common.errors.accessDeletionFailed'), error);
            this.toastService.error('common.errors.accessDeletionFailed');
          }
        })();
      });
  }
}
